<script setup lang="ts">
import { tv, type VariantProps } from 'tailwind-variants/lite'
import { computed, useTemplateRef, type Component } from 'vue'
import { LSpinnerIcon } from '../../icons'
import { LBaseButton, type BaseButtonProps } from '../button'
import { LIcon } from '../icon'

const style = tv({
  base: 'lui-icon-button',
  slots: {
    busyIndicator: 'lui-icon-button__busy-indicator',
    icon: 'lui-icon-button__icon',
  },
  variants: {
    busy: {
      true: 'lui-icon-button--busy',
    },
    size: {
      '2xs': 'lui-icon-button--size-2xs',
      xs: 'lui-icon-button--size-xs',
      sm: 'lui-icon-button--size-sm',
      md: 'lui-icon-button--size-md',
      lg: 'lui-icon-button--size-lg',
    },
    tone: {
      primary: 'lui-icon-button--tone-primary',
      secondary: 'lui-icon-button--tone-secondary',
      invert: 'lui-icon-button--tone-invert',
    },
    variant: {
      solid: 'lui-icon-button--variant-solid',
      outline: 'lui-icon-button--variant-outline',
      ghost: 'lui-icon-button--variant-ghost',
    },
  },
})

export interface IconButtonProps extends BaseButtonProps {
  /** Icon component rendered as the sole visual content of the button. */
  icon: Component
  /** Accessible name applied as `aria-label` (required because there is no visible text). */
  label: string
  /** Visual size of the icon button. */
  size?: VariantProps<typeof style>['size']
  /** Colour emphasis for the button surface and icon. */
  tone?: VariantProps<typeof style>['tone']
  /** Surface style: filled, outlined, or ghost. */
  variant?: VariantProps<typeof style>['variant']
}

/** Fired when the button is activated. */
export type IconButtonEmits = (e: 'click', event: MouseEvent) => void

const props = withDefaults(defineProps<IconButtonProps>(), {
  size: 'md',
  tone: 'primary',
  variant: 'solid',
})

const emit = defineEmits<IconButtonEmits>()

const ref = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>('ref')

defineExpose({
  focus() {
    ref.value?.focus()
  },
})

const classes = computed(() =>
  style({
    busy: props.busy,
    size: props.size,
    tone: props.tone,
    variant: props.variant,
  }),
)

const handleClick = (event: MouseEvent) => {
  emit('click', event)
}
</script>

<template>
  <LBaseButton
    :is="is"
    ref="ref"
    :class="classes.base()"
    :aria-label="label"
    :busy="busy"
    :disabled="disabled"
    :type="type"
    @click="handleClick"
  >
    <span :class="classes.busyIndicator()" aria-live="assertive">
      <LIcon :is="LSpinnerIcon" v-if="busy" aria-label="Busy" />
    </span>

    <LIcon :is="icon" :class="classes.icon()" />
  </LBaseButton>
</template>

<style>
@reference "../../css/tailwind.css";

@layer components {
  .lui-icon-button {
    @apply relative inline-flex shrink-0 cursor-pointer items-center justify-center align-middle disabled:pointer-events-none;
  }

  .lui-icon-button__busy-indicator {
    @apply absolute flex items-center;
  }

  .lui-icon-button--busy {
    @apply pointer-events-none;
  }

  .lui-icon-button--busy .lui-icon-button__icon {
    @apply opacity-0;
  }
}
</style>
